import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
  useToast,
  Link as A,
  InputGroup,
  InputLeftAddon,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { signUp } from "api/auth";
import LockIcon from "../../assets/svg/LockIcon";
import EmailIcon from "../../assets/svg/EmailIcon";
import logo from "../../assets/logo.png";

interface SignUpFormFields {
  email: string;
  password: string;
  confirmPassword: string;
  termsOfService: boolean;
}

export default function SignUpPage() {
  const [loading, setLoading] = useState<boolean>(false);
  const [form, setForm] = useState<SignUpFormFields>({
    email: "",
    password: "",
    confirmPassword: "",
    termsOfService: false,
  });
  const navigate = useNavigate();

  const toast = useToast();

  async function onSubmit(e: React.ChangeEvent<HTMLFormElement>) {
    try {
      e.preventDefault();
      if (
        form.email === "" ||
        form.password === "" ||
        form.confirmPassword === ""
      ) {
        toast({
          status: "error",
          title: "Error",
          description: "Please fill all fields!",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      //TODO: Password strength and min characters validity; also check if react hook form works now
      if (form.password !== form.confirmPassword) {
        toast({
          status: "error",
          title: "Error",
          description: "Password and Confirm Password fields do not match!",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      if (!form.termsOfService) {
        toast({
          status: "error",
          title: "Error",
          description:
            "You need to agree to the Terms of Service before creating your account!",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      setLoading(true);
      await signUp(form.email, form.password);
      setLoading(false);
    } catch (e) {
      console.error("error in login submit", e);
      toast({
        status: "error",
        title: "Error signing up",
        description: e.message,
        duration: 3000,
        isClosable: true,
      });
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  }

  return (
    <Flex minH="100vh" minW="100vw" background="white" justify="center">
      <Flex
        flex={1}
        bgGradient="linear(to-tl, gradient1.500, gradient2.500)"
        justify="center"
        alignItems="center"
      >
        <Flex align="center" color="white">
          <img src={logo} height={54} width={54} alt="cura-logo"></img>
          <Text
            fontWeight="bold"
            fontSize={32}
            fontFamily="montserrat"
            paddingX={1}
          >
            cura health
          </Text>
        </Flex>
      </Flex>

      <Flex flex={1} align="center" justifyContent="center">
        <Box p={5} w="55ch" maxWidth="100vw">
          <Stack spacing={8}>
            <Flex direction="column">
              <Text fontSize="24px" fontWeight="bold" color="black">
                Sign Up
              </Text>
              <Text fontSize="sm" fontWeight="400" color="settingText.500">
                Enter your credentials for a new Lab account
              </Text>
            </Flex>
            <form onSubmit={onSubmit}>
              <Stack spacing={4}>
                <FormControl>
                  <FormLabel htmlFor="email" color="highlight" size="sm">
                    Email
                  </FormLabel>
                  <InputGroup>
                    <InputLeftAddon bg="white">
                      <EmailIcon height="4" width="4"></EmailIcon>
                    </InputLeftAddon>
                    <Input
                      borderLeft="none"
                      name="email"
                      type="email"
                      color="highlight"
                      placeholder="Your Email Address"
                      required
                      onChange={handleChange}
                    />
                  </InputGroup>
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="password" color="highlight" size="sm">
                    Password
                  </FormLabel>
                  <InputGroup>
                    <InputLeftAddon bg="white">
                      <LockIcon height="4" width="4"></LockIcon>
                    </InputLeftAddon>
                    <Input
                      borderX="none"
                      name="password"
                      type="password"
                      required
                      placeholder="Your Password"
                      onChange={handleChange}
                    />
                  </InputGroup>
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="password" color="highlight" size="sm">
                    Confirm Password
                  </FormLabel>
                  <InputGroup>
                    <InputLeftAddon bg="white">
                      <LockIcon height="4" width="4"></LockIcon>
                    </InputLeftAddon>
                    <Input
                      borderX="none"
                      name="confirmPassword"
                      type="password"
                      required
                      placeholder="Confirm Password"
                      onChange={handleChange}
                    />
                  </InputGroup>
                </FormControl>
                <Flex justify="space-between">
                  <Flex align="center">
                    <Checkbox
                      size="sm"
                      padding={1}
                      name="termsOfService"
                      colorScheme="primary"
                      onChange={(e) =>
                        setForm({ ...form, termsOfService: e.target.checked })
                      }
                    >
                      <Text
                        paddingLeft={2}
                        fontSize="xs"
                        fontWeight="400"
                        color="settingText.500"
                      >
                        I agree to{" "}
                        <A
                          color="teal.500"
                          href="https://sites.google.com/view/curahealth/terms-and-condition"
                          target="_blank"
                          fontWeight="500"
                        >
                          Terms and Conditions
                        </A>
                      </Text>
                    </Checkbox>
                  </Flex>
                  <Flex justify="flex-end">
                    <Text fontSize="xs" mr="2">
                      Already have an account?{" "}
                      <Text
                        as="span"
                        color="primary.500"
                        fontWeight="500"
                        fontSize="sm"
                        cursor="pointer"
                        onClick={() => {
                          navigate("/login");
                        }}
                      >
                        Log In
                      </Text>
                    </Text>
                  </Flex>
                </Flex>
                <Button
                  mt={4}
                  w={"100%"}
                  type="submit"
                  isLoading={loading}
                  paddingY={6}
                >
                  SIGN UP
                </Button>
              </Stack>
            </form>
          </Stack>
        </Box>
      </Flex>
    </Flex>
  );
}
